import React from 'react';

interface ActionMenuModalProps {
  show: boolean;
  onClose: () => void;
  eventTitle: string;
  selectedServer: string;
  setSelectedServer: (value: string) => void;
  onCopyRegisterLink: () => void;
  onCopyVoteLink: () => void;
  onCopySpotlightLink: () => void;
  onCopyVerificationCode: () => void;
  onSendVotingEmail: () => void;
  onShowTimeline: () => void;
}

// Server selection component
const ServerSelection: React.FC<{
  selectedServer: string;
  setSelectedServer: (value: string) => void;
}> = ({ selectedServer, setSelectedServer }) => (
  <div className="mb-3">
    <label className="form-label fw-semibold mb-1" style={{fontSize: '0.8rem'}}>
      <i className="bi bi-server me-1"></i>Target Domain
    </label>
    <select 
      className="form-select form-select-sm border-2" 
      value={selectedServer}
      onChange={(e) => setSelectedServer(e.target.value)}
      style={{borderColor: '#dee2e6', fontSize: '0.85rem'}}
    >
      <option value="">Use current domain</option>
      <option value="https://matchmaker.ypog.law">matchmaker.ypog.law</option>
      <option value="https://partners.forum">partners.forum</option>
    </select>
  </div>
);

// Action button component
const ActionButton: React.FC<{
  icon: string;
  text: string;
  variant: 'primary' | 'success' | 'warning' | 'info' | 'danger';
  onClick: () => void;
}> = ({ icon, text, variant, onClick }) => (
  <button 
    className={`btn btn-outline-${variant} btn-sm d-flex align-items-center justify-content-start`}
    onClick={onClick}
    style={{padding: '0.5rem 0.75rem', fontSize: '0.85rem'}}
  >
    <i className={`bi ${icon} me-2`} style={{fontSize: '0.9rem'}}></i>
    <span>{text}</span>
  </button>
);

// Section header component
const SectionHeader: React.FC<{
  icon: string;
  title: string;
}> = ({ icon, title }) => (
  <h6 className="fw-semibold mb-2 text-secondary" style={{fontSize: '0.75rem'}}>
    <i className={`bi ${icon} me-1`}></i>{title}
  </h6>
);

const ActionMenuModal: React.FC<ActionMenuModalProps> = ({
  show,
  onClose,
  eventTitle,
  selectedServer,
  setSelectedServer,
  onCopyRegisterLink,
  onCopyVoteLink,
  onCopySpotlightLink,
  onCopyVerificationCode,
  onSendVotingEmail,
  onShowTimeline
}) => {
  if (!show) return null;

  // Helper function to handle action and close modal
  const handleActionAndClose = (action: () => void) => {
    action();
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="modal-backdrop show" 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1040,
          display: 'block'
        }}
        onClick={onClose}
      />

      {/* Modal */}
      <div 
        className="modal-action-container"
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '1.5rem',
          boxShadow: '0 15px 45px rgba(0, 0, 0, 0.12)',
          zIndex: 1050,
          minWidth: '320px',
          maxWidth: '420px',
          display: 'block',
          border: '1px solid #e9ecef'
        }}
      >
        {/* Modal Header */}
        <div className="text-center mb-3">
          <div className="d-inline-flex align-items-center justify-content-center bg-primary bg-opacity-10 rounded-circle mb-2" 
               style={{width: '45px', height: '45px'}}>
            <i className="bi bi-calendar-event fs-5 text-primary"></i>
          </div>
          <h6 className="mb-1 fw-bold">Event Actions</h6>
          <small className="text-muted">{eventTitle}</small>
          <button
            type="button"
            className="btn-close position-absolute top-0 end-0 mt-2 me-2"
            onClick={onClose}
          ></button>
        </div>

        {/* Modal Body */}
        <div className="modal-body p-0">
          {/* Server Selection */}
          <ServerSelection 
            selectedServer={selectedServer}
            setSelectedServer={setSelectedServer}
          />

          {/* Link Actions */}
          <div className="mb-3">
            <SectionHeader icon="bi-link-45deg" title="COPY LINKS" />
            <div className="d-grid gap-1">
              <ActionButton 
                icon="bi-person-plus"
                text="Registration Link"
                variant="primary"
                onClick={() => handleActionAndClose(onCopyRegisterLink)}
              />
              <ActionButton 
                icon="bi-ballot"
                text="Vote Link"
                variant="success"
                onClick={() => handleActionAndClose(onCopyVoteLink)}
              />
              <ActionButton 
                icon="bi-spotlight"
                text="Spotlight Link"
                variant="warning"
                onClick={() => handleActionAndClose(onCopySpotlightLink)}
              />
              <ActionButton 
                icon="bi-shield-check"
                text="Verification Code"
                variant="info"
                onClick={() => handleActionAndClose(onCopyVerificationCode)}
              />
            </div>
          </div>

          {/* Event Management */}
          <div className="mb-3">
            <SectionHeader icon="bi-gear" title="MANAGEMENT" />
            <div className="d-grid gap-1">
              <ActionButton 
                icon="bi-envelope-paper"
                text="Send Voting Email"
                variant="danger"
                onClick={() => handleActionAndClose(onSendVotingEmail)}
              />
            </div>
          </div>

          {/* User Management & Info */}
          <div className="mb-0">
            <SectionHeader icon="bi-people" title="USERS & INFO" />
            <div className="d-grid gap-1">
              <ActionButton 
                icon="bi-clock-history"
                text="Show Timeline"
                variant="info"
                onClick={() => handleActionAndClose(onShowTimeline)}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ActionMenuModal;
